import {HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {BrowserAuthError, BrowserAuthErrorCodes} from '@azure/msal-browser';

export class SecurityUtils {
	protected static AUTH_TOKEN_KEY = 'auth_token';
	protected static REFRESH_TOKEN_KEY = 'refresh_token';

	public static getHttpHeaders(): HttpHeaders {
		return new HttpHeaders({
			'Content-Type': 'application/json',
		})
	};

	public static getAuthHttpHeaders(): HttpHeaders {
		return new HttpHeaders({
			'Content-Type': 'application/json',
			'Authorization': 'Bearer ' + localStorage.getItem(SecurityUtils.AUTH_TOKEN_KEY),
		})
	};

	public static getRawAuthHttpHeaders(): HttpHeaders {
		return new HttpHeaders({
			'Authorization': 'Bearer ' + localStorage.getItem(SecurityUtils.AUTH_TOKEN_KEY),
		})
	};

	public static setAuthToken(token: string): boolean {
		localStorage.setItem(SecurityUtils.AUTH_TOKEN_KEY, token);
		return true;
	}

	public static setRefreshToken(token: string): void {
		localStorage.setItem(SecurityUtils.REFRESH_TOKEN_KEY, token);
	}

	public static getAuthToken(): string | null {
		return localStorage.getItem(SecurityUtils.AUTH_TOKEN_KEY);
	}

	public static getRefreshToken(): string | null {
		return localStorage.getItem(SecurityUtils.REFRESH_TOKEN_KEY);
	}

	public static removeAuthToken(): boolean {
		localStorage.removeItem(SecurityUtils.AUTH_TOKEN_KEY);
		localStorage.removeItem(SecurityUtils.REFRESH_TOKEN_KEY);
		return true;
	}

	/**
	 * Returns the given URL only when it is safe to use as an in-app post-login redirect: a path that stays on
	 * this origin once parsed the way the browser will follow it. The parser drops tabs and newlines and reads a
	 * backslash as a slash, so "/<tab>/evil.com" or "/\evil.com" lead to another site (open redirect).
	 */
	public static sanitizeReturnUrl(returnUrl: string | null): string | null {
		if (!returnUrl?.startsWith('/')) {
			return null;
		}
		try {
			const url = new URL(returnUrl, window.location.origin);
			return url.origin === window.location.origin ? url.pathname + url.search + url.hash : null;
		} catch {
			return null;
		}
	}

	/**
	 * The message (i18n key) of a failed Microsoft sign-in, or null when the user closed the Microsoft popup.
	 * POST /authenticate/microsoft answers 400 with the reason in `type`: a first sign-in without an e-mail Microsoft
	 * vouches for (`microsoft_email_not_verified`), or an e-mail whose Kabeen account is already linked to another
	 * Microsoft account (`bad_argument`). Anything else (refused token, popup blocked, network) is unexpected.
	 */
	public static microsoftSigninErrorKey(error: unknown): string | null {
		if (error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.userCancelled) {
			return null;
		}
		const type = error instanceof HttpErrorResponse && error.status === 400 ? error.error?.type : undefined;
		switch (type) {
			case 'microsoft_email_not_verified':
				return 'page.login.microsoftEmailNotVerified';
			case 'bad_argument':
				return 'page.login.microsoftAlreadyLinked';
			default:
				return 'alert.danger.unknownError';
		}
	}
}
